import { useState } from 'react';
import { SOURCE_LABELS, LANGUAGE_LABELS } from '@/lib/utils';
import { Owner } from '@/lib/types';

interface OwnerFormProps {
  owner?: Partial<Owner>;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

export default function OwnerForm({ owner, onSubmit, onCancel }: OwnerFormProps) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: owner?.firstName || '',
    lastName: owner?.lastName || '',
    email: owner?.email || '',
    phone: owner?.phone || '',
    phoneSecondary: owner?.phoneSecondary || '',
    whatsapp: owner?.whatsapp || '',
    preferredLanguage: owner?.preferredLanguage || 'es',
    dni: owner?.dni || '',
    iban: owner?.iban || '',
    fiscalAddress: owner?.fiscalAddress || '',
    contractModel: owner?.contractModel || '',
    commissionPercent: owner?.commissionPercent ?? '',
    applyIrpf: owner?.applyIrpf ?? (['explotacion', 'explotacion_lcm'].includes(owner?.contractModel || '') ? true : false),
    fixedRent: owner?.fixedRent ?? '',
    invoicePrefix: owner?.invoicePrefix || '',
    invoiceNextNum: owner?.invoiceNextNum ?? '',
    guestInvoicePrefix: owner?.guestInvoicePrefix || '',
    guestInvoiceNextNum: owner?.guestInvoiceNextNum ?? '',
    status: owner?.status || 'active',
    source: owner?.source || '',
    sourceDetail: owner?.sourceDetail || '',
    hasMultipleProperties: owner?.hasMultipleProperties || false,
    previouslyManagedRentals: owner?.previouslyManagedRentals || false,
    previousManagementDetail: owner?.previousManagementDetail || '',
    incomeExpectation: owner?.incomeExpectation || '',
    incomeExpectationPeriod: owner?.incomeExpectationPeriod || 'monthly',
    notes: owner?.notes || '',
    tags: owner?.tags?.join(', ') || '',
    address: {
      street: owner?.address?.street || '',
      city: owner?.address?.city || '',
      province: owner?.address?.province || 'Cádiz',
      postalCode: owner?.address?.postalCode || '',
      country: owner?.address?.country || 'España',
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        incomeExpectation: form.incomeExpectation ? Number(form.incomeExpectation) : undefined,
        commissionPercent: form.commissionPercent !== '' ? Number(form.commissionPercent) : undefined,
        fixedRent: form.fixedRent !== '' ? Number(form.fixedRent) : undefined,
        invoiceNextNum: form.invoiceNextNum !== '' ? Number(form.invoiceNextNum) : undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
  const updateAddress = (field: string, value: string) => setForm(f => ({ ...f, address: { ...f.address, [field]: value } }));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Nombre *</label>
          <input className="input" required value={form.firstName} onChange={e => updateField('firstName', e.target.value)} />
        </div>
        <div>
          <label className="label">Apellidos *</label>
          <input className="input" required value={form.lastName} onChange={e => updateField('lastName', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Email</label>
          <input type="email" className="input" value={form.email} onChange={e => updateField('email', e.target.value)} />
        </div>
        <div>
          <label className="label">DNI/NIE/CIF</label>
          <input className="input" value={form.dni} onChange={e => updateField('dni', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">IBAN</label>
          <input className="input" value={form.iban} onChange={e => updateField('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
        </div>
        <div>
          <label className="label">Modelo de contrato</label>
          <select className="input" value={form.contractModel} onChange={e => {
            const val = e.target.value;
            setForm(f => ({ ...f, contractModel: val, applyIrpf: (val === 'explotacion' || val === 'explotacion_lcm') ? true : f.applyIrpf }));
          }}>
            <option value="">Sin definir</option>
            <option value="gestion">Gestión</option>
            <option value="explotacion">Explotación</option>
            <option value="explotacion_lcm">Explotación LCM</option>
            <option value="rent2rent">Rent2Rent</option>
            <option value="ambos">Ambos</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Comisión (%)</label>
          <input type="number" className="input" min="0" max="100" step="0.5" value={form.commissionPercent} onChange={e => updateField('commissionPercent', e.target.value)} placeholder="20" />
        </div>
        {form.contractModel === 'rent2rent' && (
          <div>
            <label className="label">Renta fija mensual (€)</label>
            <input type="number" className="input" min="0" step="0.01" value={form.fixedRent} onChange={e => updateField('fixedRent', e.target.value)} />
          </div>
        )}
        {(form.contractModel === 'explotacion' || form.contractModel === 'explotacion_lcm') && (
          <div className="flex items-center gap-2 pt-1">
            <input type="checkbox" id="applyIrpf" className="w-4 h-4 rounded" checked={form.applyIrpf} onChange={e => updateField('applyIrpf', e.target.checked)} />
            <label htmlFor="applyIrpf" className="text-sm cursor-pointer">Retención IRPF 19%</label>
          </div>
        )}
      </div>

      <fieldset className="border border-gray-100 rounded-lg p-4">
        <legend className="text-sm font-medium text-sereno-dark/60 px-2">Facturación</legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Prefijo factura</label>
            <input className="input" value={form.invoicePrefix} onChange={e => updateField('invoicePrefix', e.target.value)} placeholder="SERENO26/" />
          </div>
          <div>
            <label className="label">Nº siguiente factura</label>
            <input type="number" className="input" min="1" value={form.invoiceNextNum} onChange={e => updateField('invoiceNextNum', e.target.value)} placeholder="1" />
          </div>
          <div>
            <label className="label">Prefijo factura huésped</label>
            <input className="input" value={form.guestInvoicePrefix} onChange={e => updateField('guestInvoicePrefix', e.target.value)} placeholder="H-" />
          </div>
          <div>
            <label className="label">Nº siguiente fact. huésped</label>
            <input type="number" className="input" min="1" value={form.guestInvoiceNextNum} onChange={e => updateField('guestInvoiceNextNum', e.target.value)} placeholder="1" />
          </div>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Teléfono</label>
          <input className="input" value={form.phone} onChange={e => updateField('phone', e.target.value)} />
        </div>
        <div>
          <label className="label">Teléfono secundario</label>
          <input className="input" value={form.phoneSecondary} onChange={e => updateField('phoneSecondary', e.target.value)} />
        </div>
        <div>
          <label className="label">WhatsApp</label>
          <input className="input" value={form.whatsapp} onChange={e => updateField('whatsapp', e.target.value)} placeholder="+34 600..." />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Estado</label>
          <select className="input" value={form.status} onChange={e => updateField('status', e.target.value)}>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>
        <div>
          <label className="label">Fuente de captación</label>
          <select className="input" value={form.source} onChange={e => updateField('source', e.target.value)}>
            <option value="">Seleccionar...</option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Idioma</label>
          <select className="input" value={form.preferredLanguage} onChange={e => updateField('preferredLanguage', e.target.value)}>
            {Object.entries(LANGUAGE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {form.source && (
        <div>
          <label className="label">Detalle de la fuente</label>
          <input className="input" value={form.sourceDetail} onChange={e => updateField('sourceDetail', e.target.value)} placeholder="Ej: Referido por María López" />
        </div>
      )}

      <fieldset className="border border-gray-100 rounded-lg p-4">
        <legend className="text-sm font-medium text-sereno-dark/60 px-2">Dirección personal</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-1 sm:col-span-2">
            <label className="label">Calle</label>
            <input className="input" value={form.address.street} onChange={e => updateAddress('street', e.target.value)} />
          </div>
          <div>
            <label className="label">Ciudad</label>
            <input className="input" value={form.address.city} onChange={e => updateAddress('city', e.target.value)} />
          </div>
          <div>
            <label className="label">Código postal</label>
            <input className="input" value={form.address.postalCode} onChange={e => updateAddress('postalCode', e.target.value)} />
          </div>
          <div>
            <label className="label">País</label>
            <input className="input" value={form.address.country} onChange={e => updateAddress('country', e.target.value)} />
          </div>
        </div>
      </fieldset>

      <div>
        <label className="label">Dirección fiscal</label>
        <input className="input" value={form.fiscalAddress} onChange={e => updateField('fiscalAddress', e.target.value)} placeholder="Solo si es diferente a la dirección personal" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 rounded" checked={form.hasMultipleProperties} onChange={e => updateField('hasMultipleProperties', e.target.checked)} />
          <span className="text-sm">Tiene múltiples propiedades</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 rounded" checked={form.previouslyManagedRentals} onChange={e => updateField('previouslyManagedRentals', e.target.checked)} />
          <span className="text-sm">Gestionaba alquileres antes</span>
        </label>
      </div>

      {form.previouslyManagedRentals && (
        <div>
          <label className="label">Detalle de gestión anterior</label>
          <input className="input" value={form.previousManagementDetail} onChange={e => updateField('previousManagementDetail', e.target.value)} placeholder="Ej: Gestionaba solo por Airbnb" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Expectativa de ingresos (€)</label>
          <input type="number" className="input" value={form.incomeExpectation} onChange={e => updateField('incomeExpectation', e.target.value)} />
        </div>
        <div>
          <label className="label">Período</label>
          <select className="input" value={form.incomeExpectationPeriod} onChange={e => updateField('incomeExpectationPeriod', e.target.value)}>
            <option value="monthly">Mensual</option>
            <option value="annual">Anual</option>
          </select>
        </div>
      </div>

      <div>
        <label className="label">Notas</label>
        <textarea className="input" rows={3} value={form.notes} onChange={e => updateField('notes', e.target.value)} />
      </div>

      <div>
        <label className="label">Etiquetas (separadas por coma)</label>
        <input className="input" value={form.tags} onChange={e => updateField('tags', e.target.value)} placeholder="urgente, vip, extranjero" />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? 'Guardando...' : owner?.id ? 'Guardar cambios' : 'Crear propietario'}
        </button>
      </div>
    </form>
  );
}
